import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Hand,
    Activity,
    Brain,
    CalendarDays,
    MapPin,
    Users,
    ChevronRight,
    Check,
    Minus,
    Stethoscope,
    Dumbbell,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import MagneticButton from "@/components/MagneticButton";
import ExpertSection from "@/components/ExpertSection";
import FormateursMarquee from "@/components/FormateursMarquee";

// Custom premium images generated via nano-banana
const IMG_HERO = "/images/hero-home.png";
const IMG_MANIFESTO =
    "https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/491fba79bbeeba116c7238b2b6351c9908792abddb2197f2309bf60557b65f8f.jpeg";
const IMG_TRIPTYQUE =
    "https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/4ec582450084c858beebae6c716b01f35abad247486ddfddb6279564ff2e9943.jpeg";
const IMG_TEXTURE =
    "https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/3e82204caa1bf36bf96754a733616094dddbef3e9db4e95f1a5b2baddcc7d0ff.jpeg";

const ICONS = { Hand, Activity, Brain };

const MANIFESTO_PILLARS = [
    {
        kicker: "Anticiper",
        title: "Agir avant la pathologie.",
        text: "Identifier les risques avant qu'ils ne deviennent des douleurs, des blessures ou des pertes d'autonomie. C'est le premier geste du kinésithérapeute moderne.",
    },
    {
        kicker: "Raisonner",
        title: "Une pratique fondée sur la science.",
        text: "Chaque geste, chaque exercice prescrit s'appuie sur la littérature scientifique la plus récente. Nous formons à un raisonnement clinique rigoureux, structuré, mesurable.",
    },
    {
        kicker: "Durer",
        title: "Des résultats qui tiennent.",
        text: "Le kinésithérapeute d'aujourd'hui n'est plus un réparateur : il éduque, outille, rend autonome. La Prévention Active, c'est obtenir des résultats qui s'inscrivent dans le temps.",
    },
    {
        kicker: "Motiver",
        title: "Le patient, premier acteur.",
        text: "Le patient est le premier acteur de sa prévention. Nous formons les kinés à créer l'engagement et à maintenir la motivation dans la durée — un axe différenciant de la Prévention Active.",
    },
];

function formatDate(dateIso) {
    const d = new Date(dateIso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function Home() {
    const [cursus, setCursus] = useState([]);
    const [sessions, setSessions] = useState([]);

    const { scrollY } = useScroll();
    // Hero parallax
    const heroY = useTransform(scrollY, [0, 900], [0, 220]);
    const heroScale = useTransform(scrollY, [0, 900], [1.1, 1.22]);
    const heroContentY = useTransform(scrollY, [0, 600], [0, -80]);
    const heroOverlayOpacity = useTransform(scrollY, [0, 600], [0.55, 0.95]);

    useEffect(() => {
        let mounted = true;
        api.get("/cursus").then((r) => mounted && setCursus(r.data)).catch(() => {});
        api.get("/sessions").then((r) => mounted && setSessions(r.data.slice(0, 3))).catch(() => {});
        return () => { mounted = false; };
    }, []);

    return (
        <div data-testid="page-home">
            {/* ============ HERO — cinematic ============ */}
            <section
                className="relative min-h-screen w-full overflow-hidden bg-brand-ink text-white"
                data-testid="hero-section"
            >
                {/* Parallax + Ken Burns */}
                <motion.div
                    className="absolute inset-0 -top-20 -bottom-20"
                    style={{ y: heroY, scale: heroScale }}
                >
                    <motion.img
                        src={IMG_HERO}
                        alt="Cursus Physio — Prévention Active, panorama signature"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        className="h-full w-full object-cover object-center"
                        initial={{ opacity: 0, scale: 1 }}
                        animate={{
                            opacity: 1,
                            scale: [1, 1.05, 1],
                        }}
                        transition={{
                            opacity: { duration: 1.8, ease: [0.22, 1, 0.36, 1] },
                            scale: { duration: 24, repeat: Infinity, ease: "easeInOut" },
                        }}
                    />
                </motion.div>
                {/* Overlays — assure lisibilité du texte blanc sur image variée */}
                <motion.div
                    className="absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_60%,rgba(10,10,10,0.25)_0%,rgba(10,10,10,0.55)_55%,rgba(10,10,10,0.85)_100%)]"
                    style={{ opacity: heroOverlayOpacity }}
                />
                {/* Gradient bottom-up pour lisibilité CTA + titre */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/15" />
                {/* Halo orange ADN visuel (côté gauche) */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-32 -left-32 h-[520px] w-[520px] rounded-full bg-brand-orange/10 blur-[160px]"
                />
                {/* Surcouche mobile plus dense pour garantir contraste sur petit écran */}
                <div className="absolute inset-0 bg-black/25 sm:bg-transparent" />

                {/* Content */}
                <motion.div
                    style={{ y: heroContentY }}
                    className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-32 pt-40 md:pb-40"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                        className="inline-flex items-center self-start rounded-full border border-white/20 bg-white/[0.06] px-6 py-3 text-sm uppercase tracking-[0.28em] backdrop-blur sm:text-base"
                        data-testid="hero-kicker"
                    >
                        <span className="font-semibold text-white">N&apos;attendez plus&nbsp;!</span>
                    </motion.div>

                    <h1 className="mt-10 max-w-6xl font-display font-normal leading-[0.92] tracking-tightest">
                        <motion.span
                            initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
                            className="block text-[44px] sm:text-6xl lg:text-[88px]"
                        >
                            Un <em className="italic text-brand-orange">Cursus</em> stratégique
                        </motion.span>
                        <motion.span
                            initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
                            className="block text-[44px] sm:text-6xl lg:text-[88px]"
                        >
                            pour dynamiser votre cabinet.
                        </motion.span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1 }}
                        className="mt-10 max-w-2xl text-lg leading-[1.55] text-white/75 md:text-xl"
                    >
                        La <span className="font-semibold text-white">Prévention Active</span>, une <em className="italic text-brand-orange">réelle opportunité</em>
                        <br className="hidden lg:block" />{" "}
                        pour l&apos;avenir immédiat de la profession kiné.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.2 }}
                        className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <MagneticButton strength={0.25}>
                            <Link
                                to="/expertise-pa"
                                data-testid="hero-cta-expertise"
                                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-shadow hover:shadow-xl hover:shadow-brand-orange/30"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Expertise
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </span>
                                <span className="absolute inset-0 origin-left scale-x-0 bg-[#d9772c] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
                            </Link>
                        </MagneticButton>
                        <MagneticButton strength={0.18}>
                            <Link
                                to="/diversification-activite"
                                data-testid="hero-cta-hors-nomenclature"
                                className="group inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-sm font-semibold text-white transition-all hover:border-brand-orange hover:bg-brand-orange/10"
                            >
                                Hors nomenclature
                                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                        <MagneticButton strength={0.15}>
                            <Link
                                to="/modele-economique"
                                data-testid="hero-cta-profits"
                                className="group inline-flex items-center gap-2 rounded-full bg-white/[0.06] px-8 py-4 text-sm font-semibold text-white/85 backdrop-blur transition-all hover:bg-white/[0.12] hover:text-white"
                            >
                                Profits
                                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                    </motion.div>

                    {/* Bouncing scroll indicator */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.8 }}
                        className="pointer-events-none absolute inset-x-0 bottom-10 flex items-center justify-center"
                    >
                        <motion.div
                            animate={{ y: [0, 10, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50"
                        >
                            <span>Scroll</span>
                            <div className="flex h-10 w-[22px] items-start justify-center rounded-full border border-white/30 p-1">
                                <motion.div
                                    className="h-2 w-[2px] rounded-full bg-brand-orange"
                                    animate={{ y: [0, 10, 0] }}
                                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </section>

            {/* ============ SIGNATURE — bold statement ============ */}
            <section className="relative overflow-hidden bg-brand-ink py-32 text-white md:py-40 grain" data-testid="section-signature">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-orange">
                        Notre signature
                    </div>
                    <h2 className="mt-8 font-display font-normal leading-[1.05] tracking-tightest">
                        <Reveal>
                            <span className="block text-[44px] sm:text-6xl lg:text-[88px]">
                                Agir en véritable <em className="italic text-brand-orange">kiné entrepreneur</em>
                            </span>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <span className="block text-[44px] sm:text-6xl lg:text-[88px]">
                                de sa santé professionnelle.
                            </span>
                        </Reveal>
                    </h2>
                    <Reveal delay={0.45}>
                        <p className="mt-12 max-w-3xl text-lg leading-relaxed text-white/70">
                            <strong className="text-white">CURSUS PHYSIO</strong> est un système
                            complet de montée en compétences et de développement de cabinet, conçu
                            spécifiquement pour les kinésithérapeutes, pour une transformation
                            professionnelle et entrepreneuriale.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ============ LA MÉTHODE CURSUS ============ */}
            <section className="relative bg-white py-24 md:py-32" data-testid="section-methode-cursus">
                <div className="mx-auto max-w-6xl px-6 text-center">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-orange">
                            La méthode Cursus
                        </span>
                        <h2 className="mt-6 font-display text-[32px] font-normal leading-[1.1] tracking-tightest sm:text-[40px] lg:text-[52px] lg:leading-[1.12]">
                            Un positionnement <em className="italic text-brand-orange">premium</em>{" "}
                            <span className="text-brand-orange" aria-hidden="true">/</span>
                            <br className="hidden lg:block" />{" "}
                            Une offre différenciante{" "}
                            <span className="text-brand-orange" aria-hidden="true">/</span>{" "}
                            Un revenu maîtrisé.
                        </h2>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-ink/75 md:text-lg">
                            Avec pour dénominateur commun, la{" "}
                            <strong className="text-brand-ink">Prévention Active.</strong>
                        </p>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div className="mt-14 grid items-stretch gap-4 sm:grid-cols-[1fr_auto_1fr_auto_1fr]" data-testid="cursus-flow-arrows">
                            {[
                                { n: "01", label: "SOMA", text: "Maîtriser le corps en mouvement." },
                                { n: "02", label: "NEURO-KINESIS", text: "Évaluer, mesurer, décider." },
                                { n: "03", label: "VITAL", text: "Optimiser la vie pour durer." },
                            ].map((step, i, arr) => (
                                <Fragment key={step.n}>
                                    <div className="flex h-full flex-col rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                                        <div className="flex items-center justify-center">
                                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-white font-display text-lg shadow-[0_10px_25px_rgba(232,135,60,0.25)]">
                                                {step.n}
                                            </div>
                                        </div>
                                        <div className="mt-5 font-display text-xl leading-tight text-brand-ink">{step.label}</div>
                                        <div className="mt-3 text-sm leading-relaxed text-brand-ink/70">{step.text}</div>
                                    </div>
                                    {i < arr.length - 1 && (
                                        <div
                                            className="flex items-center justify-center font-display text-5xl font-normal leading-none text-brand-orange sm:text-6xl"
                                            aria-hidden
                                            data-testid={`cursus-flow-plus-${i}`}
                                        >
                                            +
                                        </div>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.35}>
                        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-brand-ink/75">
                            <span className="inline-flex items-center gap-2">
                                <span className="text-brand-orange">✓</span> Une{" "}
                                <strong className="text-brand-orange">stratégie gagnante</strong>
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="text-brand-orange">✓</span> Acquisition du statut{" "}
                                <strong className="text-brand-orange">Expert PA</strong>
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="text-brand-orange">✓</span> Optimisation des{" "}
                                <strong className="text-brand-orange">revenus</strong>
                            </span>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ============ BANDEAU IMAGE — Société active (entre méthode et manifesto) ============ */}
            <section
                className="relative bg-white pb-4 pt-0"
                data-testid="home-band-society"
                aria-hidden="true"
            >
                <div className="mx-auto max-w-7xl px-6">
                    <div
                        className="relative w-full overflow-hidden rounded-3xl bg-brand-sand"
                        style={{ aspectRatio: "21/9" }}
                    >
                        <img
                            src="/images/expertise-society.jpg"
                            alt="Prévention Active — société en mouvement"
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* ============ MANIFESTO — RECONQUÊTE ============ */}
            <section
                className="relative overflow-hidden bg-brand-sand py-28 md:py-36"
                data-testid="section-manifesto"
            >
                <div className="mx-auto max-w-6xl px-6">
                    {/* --- Bloc 1 : Eyebrow + Titre principal --- */}
                    <Reveal className="text-center">
                        <span
                            className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange"
                            data-testid="manifesto-eyebrow"
                        >
                            Reconquête du marché du «&nbsp;coaching&nbsp;»
                        </span>
                        <h2
                            className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[40px] lg:text-[52px] xl:text-[56px] lg:leading-[1.1]"
                            data-testid="manifesto-title"
                        >
                            Reprendre les parts de marché
                            <br className="hidden lg:block" />{" "}
                            <em className="italic text-brand-orange">qui vous appartiennent</em>{" "}
                            avec Cursus Physio.
                        </h2>
                    </Reveal>

                    {/* --- Séparateur filet orange --- */}
                    <Reveal delay={0.15}>
                        <div className="mx-auto mt-16 h-px w-24 bg-brand-orange/60" aria-hidden="true" />
                    </Reveal>

                    {/* --- Bloc 2 : Argumentaire central --- */}
                    <Reveal delay={0.2} className="mx-auto mt-16 max-w-3xl text-center">
                        <h3
                            className="font-display text-[26px] font-normal leading-[1.2] text-brand-ink sm:text-3xl lg:text-[34px]"
                            data-testid="manifesto-subtitle"
                        >
                            La <span className="text-brand-orange">légitimité</span> est de votre côté
                            <br className="hidden lg:block" />{" "}
                            pour passer du patient au client.
                        </h3>
                        <p
                            className="mt-8 text-base leading-[1.75] text-brand-ink/80 sm:text-[17px] sm:leading-[1.8]"
                            data-testid="manifesto-paragraph"
                        >
                            Les kinésithérapeutes sont les{" "}
                            <strong className="font-semibold text-brand-ink">
                                professionnels les plus légitimes
                            </strong>{" "}
                            pour accompagner le public dans les domaines du bien-être et de la pratique sportive.
                            Votre formation médicale, votre connaissance du corps humain et votre expertise clinique
                            vous placent{" "}
                            <strong className="font-semibold text-brand-ink">
                                bien au-dessus de tout coach sportif
                            </strong>
                            . <strong className="font-semibold text-brand-orange">CURSUS PHYSIO</strong> vous donne
                            les outils, la structure et le positionnement pour reconquérir ce terrain, en toute
                            légitimité, avec toute votre autorité professionnelle.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ============ COMPARAISON TERRAIN — Kiné vs Coach ============ */}
            <section
                className="relative overflow-hidden bg-white py-28 md:py-36"
                data-testid="section-compare"
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-brand-orange/[0.07] blur-[140px]"
                />

                <div className="relative mx-auto max-w-6xl px-6">
                    {/* Header */}
                    <Reveal className="text-center">
                        <span
                            className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange"
                            data-testid="compare-eyebrow"
                        >
                            Comparaison terrain
                        </span>
                        <h2
                            className="mx-auto mt-8 max-w-5xl font-display text-[28px] font-normal leading-[1.15] tracking-tightest text-brand-ink sm:text-[36px] lg:text-[44px] xl:text-[48px] lg:leading-[1.16]"
                            data-testid="compare-title"
                        >
                            Pourquoi le kinésithérapeute
                            <br className="hidden lg:block" />{" "}
                            est <em className="italic text-brand-orange">l&apos;acteur le plus légitime</em>
                            <br className="hidden lg:block" />{" "}
                            du marché du bien-être, de la performance
                            <br className="hidden lg:block" />{" "}
                            et de l&apos;accompagnement préventif&nbsp;?
                        </h2>
                    </Reveal>

                    {/* Grille comparatif */}
                    <div className="mt-20 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-[1fr_auto_1fr] lg:gap-6">
                        {/* COL 1 — Kiné Expert */}
                        <Reveal>
                            <div
                                className="relative h-full rounded-3xl border border-brand-ink/10 bg-[#F5F2EE] p-8 sm:p-10"
                                data-testid="compare-kine-card"
                            >
                                <div className="flex items-center gap-4">
                                    <span
                                        className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg"
                                        aria-hidden="true"
                                    >
                                        <Stethoscope className="h-5 w-5" />
                                    </span>
                                    <span className="inline-flex items-center rounded-full border border-brand-orange/40 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                        Expert
                                    </span>
                                </div>
                                <h3 className="mt-8 font-display text-3xl font-normal leading-tight text-brand-ink sm:text-[32px]">
                                    Kinésithérapeute Expert
                                </h3>
                                <ul
                                    className="mt-8 space-y-4 text-[15px] leading-relaxed text-brand-ink/85 sm:text-base"
                                    data-testid="compare-kine-list"
                                >
                                    {[
                                        "Formation universitaire",
                                        "Diplôme d'État",
                                        "Expertise médicale reconnue",
                                        "Responsabilité professionnelle encadrée",
                                    ].map((item, idx) => (
                                        <li
                                            key={idx}
                                            className="flex items-start gap-3"
                                            data-testid={`compare-kine-item-${idx}`}
                                        >
                                            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                            </span>
                                            <span className="font-medium">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                        {/* SÉPARATEUR vs. */}
                        <Reveal delay={0.2}>
                            <div
                                className="flex items-center justify-center lg:h-full lg:px-2"
                                data-testid="compare-vs-separator"
                            >
                                <div className="relative flex items-center justify-center">
                                    {/* Trait fin vertical desktop */}
                                    <div
                                        aria-hidden="true"
                                        className="absolute left-1/2 hidden h-32 w-px -translate-x-1/2 bg-brand-line lg:block lg:h-48 lg:[mask-image:linear-gradient(to_bottom,transparent,black_30%,black_70%,transparent)]"
                                        style={{ top: "calc(50% - 96px)" }}
                                    />
                                    <span className="relative z-10 rounded-full bg-white px-4 font-display text-4xl italic text-brand-orange sm:text-5xl lg:text-[56px]">
                                        vs.
                                    </span>
                                </div>
                            </div>
                        </Reveal>

                        {/* COL 2 — Coach Sportif */}
                        <Reveal delay={0.3}>
                            <div
                                className="relative h-full rounded-3xl border border-brand-ink/10 bg-[#F5F2EE] p-8 sm:p-10"
                                data-testid="compare-coach-card"
                            >
                                <div className="flex items-center gap-4">
                                    <span
                                        className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-ink/10 text-brand-ink/60"
                                        aria-hidden="true"
                                    >
                                        <Dumbbell className="h-5 w-5" />
                                    </span>
                                    <span className="inline-flex items-center rounded-full border border-brand-ink/15 bg-brand-sand/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-ink/55">
                                        Coach
                                    </span>
                                </div>
                                <h3 className="mt-8 font-display text-3xl font-normal leading-tight text-brand-ink/85 sm:text-[32px]">
                                    Coach Sportif
                                </h3>
                                <ul
                                    className="mt-8 space-y-4 text-[15px] leading-relaxed text-brand-ink/65 sm:text-base"
                                    data-testid="compare-coach-list"
                                >
                                    {[
                                        "Formation courte",
                                        "Aucune compétence médicale",
                                        "Pas de diagnostic possible",
                                        "Terrain occupé par défaut",
                                    ].map((item, idx) => (
                                        <li
                                            key={idx}
                                            className="flex items-start gap-3"
                                            data-testid={`compare-coach-item-${idx}`}
                                        >
                                            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-ink/[0.06] text-brand-ink/45">
                                                <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            </span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ STATEMENT FINAL — LE PUNCH ============ */}
            <section
                className="relative overflow-hidden bg-[#33333A] py-32 text-white md:py-44 grain"
                data-testid="section-manifesto-punch"
            >
                {/* Halos orange */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[160px]"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-40 right-0 h-[420px] w-[420px] rounded-full bg-brand-orange/10 blur-[140px]"
                />

                <div className="relative mx-auto max-w-6xl px-6 text-center">
                    <div
                        className="space-y-3 font-display font-normal leading-[1.04] tracking-tightest sm:space-y-4"
                        data-testid="manifesto-statement"
                    >
                        <Reveal>
                            <p className="text-[34px] text-white/85 sm:text-5xl lg:text-[72px]">
                                Vous avez la formation.
                            </p>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="text-[34px] text-white/85 sm:text-5xl lg:text-[72px]">
                                Vous avez la légitimité.
                            </p>
                        </Reveal>
                        <Reveal delay={0.36}>
                            <p className="text-[34px] text-white/85 sm:text-5xl lg:text-[72px]">
                                Il vous manquait le système.
                            </p>
                        </Reveal>
                        <Reveal delay={0.6}>
                            <p
                                className="pt-4 text-[36px] italic text-brand-orange sm:text-[56px] lg:text-[80px] sm:pt-6"
                                data-testid="manifesto-statement-final"
                            >
                                C&apos;est maintenant résolu&nbsp;!
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ Devenez Expert PA ============ */}
            <ExpertSection />

            {/* ============ BANDEAU IMAGE — Débouchés de la PA ============ */}
            <section
                className="relative bg-white py-14"
                data-testid="home-band-debouches"
                aria-hidden="true"
            >
                <div className="mx-auto max-w-7xl px-6">
                    <div
                        className="relative w-full overflow-hidden rounded-3xl bg-[#F5F2EE]"
                        style={{ aspectRatio: "21/9" }}
                    >
                        <img
                            src="/images/divers-debouches.jpg"
                            alt="8 débouchés de la Prévention Active"
                            loading="lazy"
                            decoding="async"
                            className="mx-auto block h-full w-full max-w-5xl object-contain"
                        />
                    </div>
                </div>
            </section>

            {/* ============ 3 CURSUS — triptyque background ============ */}
            <section className="relative overflow-hidden bg-brand-sand py-28 md:py-36" data-testid="section-cursus">
                {/* Subtle triptyque backdrop */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.06] [mask-image:radial-gradient(60%_60%_at_50%_40%,black,transparent)]"
                    style={{
                        backgroundImage: `url(${IMG_TRIPTYQUE})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="relative mx-auto max-w-7xl px-6">
                    <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                        <Reveal className="max-w-3xl">
                            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-orange">
                                Nos 3 cursus
                            </span>
                            <h2 className="mt-6 font-display text-[40px] font-normal leading-[1.05] tracking-tightest sm:text-5xl lg:text-[58px] xl:text-6xl">
                                1 Cursus,
                                <br className="hidden lg:block" />{" "}
                                <em className="italic text-brand-orange">3 axes de Prévention Active.</em>
                            </h2>
                            <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-ink/75">
                                Chaque Cursus forme le thérapeute à{" "}
                                <strong className="text-brand-ink">diagnostiquer</strong>,{" "}
                                <strong className="text-brand-ink">solutionner</strong> et{" "}
                                <strong className="text-brand-ink">accompagner</strong> dans tous les domaines couverts par la Prévention Active.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <MagneticButton strength={0.2}>
                                <Link
                                    to="/expertise-pa"
                                    data-testid="link-see-all-cursus"
                                    className="group inline-flex items-center gap-2 rounded-full border border-brand-ink/20 px-5 py-2.5 text-sm font-semibold text-brand-ink hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                                >
                                    Voir tous les cursus
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </MagneticButton>
                        </Reveal>
                    </div>

                    <StaggerGroup stagger={0.14} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {cursus.map((c, idx) => {
                            const Icon = ICONS[c.icon] || Hand;
                            return (
                                <StaggerItem key={c.id}>
                                    <Link
                                        to={`/cursus/${c.slug}`}
                                        data-testid={`card-cursus-${c.slug}`}
                                        className="group relative block h-full overflow-hidden rounded-3xl border border-brand-line bg-white shadow-premium transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_40px_80px_rgba(232,135,60,0.15)]"
                                    >
                                        <div className="relative h-60 w-full overflow-hidden">
                                            <img
                                                src={c.image_url}
                                                alt={c.name}
                                                loading="lazy"
                                                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                            <div className="absolute inset-0 bg-brand-orange/0 transition-colors duration-500 group-hover:bg-brand-orange/10" />
                                            <div className="absolute left-6 top-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div className="absolute bottom-5 right-6 font-display text-4xl text-white/95 leading-none">
                                                0{idx + 1}
                                            </div>
                                            <div className="absolute bottom-5 left-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/80">
                                                Axe de prévention
                                            </div>
                                        </div>

                                        <div className="p-8">
                                            <h3 className="font-display text-2xl leading-tight text-brand-ink md:text-[26px]">
                                                {c.name.replace("Cursus ", "")}
                                            </h3>
                                            <p className="mt-3 text-sm italic text-brand-orange">{c.tagline}</p>
                                            <p className="mt-5 line-clamp-3 text-sm leading-relaxed text-brand-ink/65">
                                                {c.description}
                                            </p>
                                            <div className="mt-8 flex items-center justify-between border-t border-brand-line pt-5">
                                                <span className="text-xs uppercase tracking-wider text-brand-smoke">
                                                    3 formations · 21h
                                                </span>
                                                <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink group-hover:text-brand-orange">
                                                    Voir le cursus
                                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                </StaggerItem>
                            );
                        })}
                    </StaggerGroup>
                </div>
            </section>

            {/* ============ FORMATEURS — Marquee infinite ============ */}
            <section className="relative bg-white py-28 md:py-36" data-testid="section-formateurs">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="text-center">
                        <span
                            className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange"
                            data-testid="formateurs-eyebrow"
                        >
                            Notre équipe formation
                        </span>
                        <h2
                            className="mx-auto mt-8 max-w-6xl font-display text-[36px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-[48px] lg:text-[64px] xl:text-[72px] lg:leading-[1.08]"
                            data-testid="formateurs-title"
                        >
                            Des formateurs
                            <br className="hidden lg:block" />{" "}
                            qui <em className="italic text-brand-orange">anticipent et diversifient</em>
                            <br className="hidden lg:block" />{" "}
                            les approches de demain.
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-16">
                    <FormateursMarquee />
                </div>

                <div className="mx-auto mt-14 max-w-7xl px-6 text-center">
                    <Reveal delay={0.2}>
                        <MagneticButton strength={0.2}>
                            <Link
                                to="/nos-formateurs"
                                data-testid="link-all-formateurs"
                                className="group inline-flex items-center gap-2 rounded-full border border-brand-ink/20 px-6 py-3 text-sm font-semibold text-brand-ink transition-all hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                            >
                                Découvrir tous nos formateurs
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                    </Reveal>
                </div>
            </section>

            {/* ============ PROCHAINES SESSIONS ============ */}
            <section className="bg-brand-ink text-white py-28 md:py-36 grain" data-testid="section-upcoming-sessions">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                        <Reveal>
                            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-orange">
                                Prochaines sessions
                            </span>
                            <h2 className="mt-6 font-display text-[44px] font-normal leading-[0.95] tracking-tightest sm:text-5xl lg:text-7xl">
                                Réservez <em className="italic text-white/60">votre place</em>.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <Link
                                to="/sessions"
                                data-testid="link-all-sessions"
                                className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white/90 hover:border-white/60 hover:text-white"
                            >
                                Toutes les sessions
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <StaggerGroup stagger={0.12} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {sessions.map((s) => (
                            <StaggerItem key={s.id}>
                                <Link
                                    to={`/inscription/${s.id}`}
                                    data-testid={`home-session-${s.id}`}
                                    className="group block h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/40 hover:bg-white/[0.06]"
                                >
                                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-brand-orange">
                                        <CalendarDays className="h-3.5 w-3.5" /> {formatDate(s.start_date)}
                                    </div>
                                    <h3 className="mt-4 font-display text-2xl leading-tight">{s.formation_name}</h3>
                                    <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/70">
                                        <span className="inline-flex items-center gap-1.5">
                                            <MapPin className="h-3.5 w-3.5 text-brand-orange" /> {s.city}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <Users className="h-3.5 w-3.5 text-brand-orange" /> {s.available_seats} places
                                        </span>
                                    </div>
                                    <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-brand-orange">
                                        S'inscrire
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            </StaggerItem>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* ============ FINAL CTA ============ */}
            <section className="relative overflow-hidden bg-brand-ink text-white" data-testid="section-final-cta">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: `url(${IMG_TEXTURE})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/80 via-brand-ink/90 to-brand-ink" />
                <div className="relative mx-auto max-w-5xl px-6 py-28 text-center md:py-36">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">
                            Prévention Active
                        </span>
                        <h2 className="mt-6 font-display text-[40px] font-normal leading-[1.04] tracking-tightest sm:text-5xl lg:text-7xl xl:text-[88px] lg:leading-[1.06]">
                            Rejoignez
                            <br className="hidden lg:block" />{" "}
                            la <em className="italic text-brand-orange">1<sup className="text-[0.55em] align-super">re</sup> communauté</em>
                            <br className="hidden lg:block" />{" "}
                            d&apos;Experts en Prévention Active.
                        </h2>
                        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <MagneticButton strength={0.25}>
                                <Link
                                    to="/expertise-pa"
                                    data-testid="final-cta-cursus"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                                >
                                    Explorer les cursus
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </MagneticButton>
                            <MagneticButton strength={0.18}>
                                <Link
                                    to="/contact"
                                    data-testid="final-cta-contact"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-semibold text-white/90 transition-all hover:border-white/60 hover:text-white"
                                >
                                    Nous contacter
                                </Link>
                            </MagneticButton>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
